import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { aLogEvent, AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import {
  MOBILE_CALENDAR_DIALOG_HEIGHT,
  MOBILE_DIALOG_CONFIG,
  PLANNING_MIN_WIDTH_SIDE,
  SavedToLocalstorage,
} from '@app/shared/constants';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { CheckListPlanning } from '@radioking/onboarding-checklist';
import { SchedulerCoreState } from '@radioking/scheduler-core';
import {
  CloseCurrentPanel,
  OpenSpecificPanel,
  PANELS_KEYS,
  PanelsState,
  TryActionUnlessPanelIsBlocking,
} from '@radioking/shared/pannel-manager';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';
import { DateSelectDatesChanged } from '../../models/date-select.model';

import { MatDialog } from '@angular/material/dialog';
import { MobileDatePickerDialogComponent } from 'libs/ui/mobile-date-picker-dialog/mobile-date-picker-dialog.component';
import { CalendarDatesChanged } from '../../models/planning.model';
import { ClearPlanning, SetCurrentDay } from '../../states/planning.actions';
import { PlanningState } from '../../states/planning.state';

@Component({
  selector: 'rk-planning-header',
  templateUrl: './planning-header.component.html',
  styleUrls: ['./planning-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningHeaderComponent implements OnInit, OnDestroy {
  @Select(PlanningState.currentDay)
  currentDay$: Observable<moment.Moment>;

  @Input()
  calendarDatesChanged: CalendarDatesChanged;

  @Output()
  readonly addSlot = new EventEmitter<void>();

  @Output()
  readonly dateSelectDatesChanged = new EventEmitter<DateSelectDatesChanged>();

  @Output()
  readonly calendarPrevious = new EventEmitter<void>();

  @Output()
  readonly calendarNext = new EventEmitter<void>();

  /**
   * Set planning to current day/week
   */
  @Output()
  readonly calendarToday = new EventEmitter<void>();

  @Select(SchedulerCoreState.hasNotEmptyPlaylists)
  hasPlaylists$: Observable<boolean>;
  @Select(ProgramsState.hasNotEmptyPrograms)
  hasPrograms$: Observable<boolean>;

  pulse = false;
  subscription = new Subscription();

  planningStorageKey = SavedToLocalstorage.PLANNING_SIDE_PANEL;

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.subscription.add(
      combineLatest([this.hasPrograms$, this.hasPlaylists$])
        .pipe(
          filter(([programs, playlists]) => programs || playlists),
          take(1),
        )
        .subscribe(([programs, playlists]) => {
          if (
            (programs || playlists) &&
            window.innerWidth > PLANNING_MIN_WIDTH_SIDE &&
            localStorage.getItem(this.planningStorageKey)
          ) {
            this.addSlotPanel();
          }
        }),
    );
    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(CheckListPlanning)).subscribe(() => {
        this.pulse = true;
        this.cdr.detectChanges();
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * "Add slot" button clicked
   */
  addSlotClicked(): void {
    aLogEvent(AMPLITUDE_EVENT.PLANNING.CTA_ADD);
    this.pulse = false;
    this.cdr.detectChanges();
    this.addSlot.next();
  }

  /**
   * Selected date changed
   */
  dateSelectDatesUpdated(event: DateSelectDatesChanged): void {
    this.clearPlanning();
    this.dateSelectDatesChanged.emit(event);
  }

  /**
   * "Previous" button clicked
   */
  calendarPreviousClicked(): void {
    this.clearPlanning();
    this.calendarPrevious.next();
  }

  /**
   * "Next" button clicked
   */
  calendarNextClicked(): void {
    this.clearPlanning();
    this.calendarNext.next();
  }

  /**
   * "Today" button clicked
   */
  calendarTodayClicked(): void {
    this.clearPlanning();
    this.calendarToday.next();
  }

  addSlotPanel(): void {
    const panelsOpened = this.store.selectSnapshot(PanelsState.currentPanel);
    if (panelsOpened === PANELS_KEYS.PLANNING_SLOT_ADD) {
      this.closePanel();
      return;
    }

    this.store.dispatch(
      new TryActionUnlessPanelIsBlocking(
        new OpenSpecificPanel(PANELS_KEYS.PLANNING_SLOT_ADD),
      ),
    );
  }

  @Dispatch()
  clearPlanning = () => new ClearPlanning();

  @Dispatch()
  closePanel = () => new CloseCurrentPanel();

  selectDate() {
    const dialog = this.dialog.open(MobileDatePickerDialogComponent, {
      ...MOBILE_DIALOG_CONFIG,
      height: MOBILE_CALENDAR_DIALOG_HEIGHT,
      data: { date: this.store.selectSnapshot(PlanningState.currentDay) },
    });
    dialog
      .afterClosed()
      .pipe(filter(date => !!date))
      .subscribe((selectedDate: moment.Moment) => {
        this.setCurrentDay(selectedDate);
      });
  }

  @Dispatch()
  setCurrentDay = (selectedDate: moment.Moment) => new SetCurrentDay(selectedDate);
}
